import { useState, useEffect } from "react";
import {
  Button,
  Icon,
  TablePaginantion,
  useToast,
} from "@elaniintc/goes-design-system";
import NoData from "@/components/ui/no-data";
import { useAuthorization } from "@/hooks/use-authorization";
import { parseAsString, useQueryState } from "nuqs";
import {
  sortDirectionEnum,
  type FilterTypeList,
  type SortDirectionType,
  type SortOptionsTypes,
  type CurrentFiltersType,
  filterTypesEnum,
  type MultiEntitySelections,
} from "@/types/common/filters";
import { usePagination } from "@/hooks/usePagination";
import Filter from "@/components/ui/filters";
import { Permissions } from "@/utils/permissions";
import SearchInput from "@/components/common/search-input";
import {
  useActivateTemplate,
  useDeactivateTemplate,
  useGetTemplates,
} from "@/queries/templates/templates.api";
import { useDebounceValue } from "usehooks-ts";
import { Skeleton } from "@/components/ui/skeleton";
import { NavLink } from "react-router";
import type {
  DocumentDetails,
  TemplateResource,
} from "@/types/entities/templates";
import SortDropdown from "@/pages/procesos/components/SortDropdown";
import DocumentCard from "./DocumentCard";
import DocumentModal from "./NewDocumentTemplateModal";
import DeleteTemplateModal from "./DeleteTemplateModal";
import ActivateTemplateModal from "./ActiveteTemplateModal";

const sortFields: SortOptionsTypes[] = [
  { label: "Nombre", value: "display_name" },
  { label: "Estatus", value: "status" },
  { label: "Fecha de actualización", value: "updated_at" },
  { label: "Fecha de creación", value: "created_at" },
];

const FilterFields: FilterTypeList[] = [
  {
    label: "Fecha de creación",
    type: filterTypesEnum.dates,
    field: "created_at",
  },
  {
    label: "Última Actualización",
    type: filterTypesEnum.dates,
    field: "updated_at",
  },
  {
    label: "Estatus",
    type: filterTypesEnum.status,
    field: "status",
    options: [
      { label: "Activa", field: "active" },
      { label: "Inactiva", field: "inactive" },
      { label: "En Construcción", field: "draft" },
    ],
  },
];

const DocumentTemplatesList = () => {
  const { hasPermission } = useAuthorization();
  const canCreate = hasPermission(Permissions.processes.create); //TODO Cambiar a data-templates
  const { showToast } = useToast();
  const [page, setPage] = useState(1);
  const [modalState, setModalState] = useState<
    "details" | "delete" | "activate" | "idle"
  >("idle");

  const [selectedTemplate, setSelectedTemplate] = useState<
    TemplateResource | undefined
  >();
  const [query, setQuery] = useQueryState("searchDocs", parseAsString);
  const [search, setSearch] = useState(query || "");
  const [deferredQuery] = useDebounceValue(search?.trim() || "", 500);
  const [orderField, setOrderField] = useState<string | undefined>(
    "created_at",
  );
  const [orderDirection, setOrderDirection] = useState<SortDirectionType>(
    sortDirectionEnum.desc,
  );
  const [filters, setFilters] = useState<CurrentFiltersType[] | []>([]);
  const [selectedEntities, setSelectedEntities] =
    useState<MultiEntitySelections>({});

  const apiFilters = filters.reduce(
    (acc, filter) => {
      Object.entries(filter).forEach(([key, value]) => {
        if (value) {
          acc[key] = value;
        }
      });
      return acc;
    },
    {} as Record<string, string>,
  );

  const {
    data: templatesData,
    isLoading,
    isError,
    refetch,
  } = useGetTemplates({
    page,
    per_page: 15,
    order_by: orderField,
    sort_direction: orderDirection,
    ...(deferredQuery ? { search: deferredQuery } : {}),
    filters: apiFilters,
  });

  const { mutateAsync: activateMutation, isPending: isActivating } =
    useActivateTemplate();
  const { mutateAsync: deActivateMutation, isPending: isDeactivating } =
    useDeactivateTemplate();

  const {
    currentPage,
    totalItems,
    hasNextPage,
    hasPreviousPage,
    onNext,
    onPrevious,
    onPageChange,
    totalPages,
  } = usePagination({
    meta: templatesData?.meta,
    links: templatesData?.links,
    onPageUpdate: (page) => {
      setPage(page);
    },
  });

  const NoDataToShow = () => {
    return (
      <div className="flex flex-col justify-center items-center min-h-[calc(100vh-210px)]">
        <NoData
          title="Aún no existen plantillas para documentos"
          description="Crea una plantilla personalizada"
        >
          {canCreate && (
            <Button
              variant="default"
              size="sm"
              className="items-center gap-2 rounded-lg cursor-pointer leading-2xl"
              onClick={() => handleOpenCreate()}
            >
              Nueva plantilla
              <Icon type="far" name="plus" className="text-current" />
            </Button>
          )}
        </NoData>
      </div>
    );
  };

  const templates = templatesData?.data || [];
  const actualPageSize = templates.length;

  const handleApplySort = (
    field: string | undefined,
    direction: SortDirectionType,
  ) => {
    setPage(1);
    setOrderField(field);
    setOrderDirection(direction);
  };

  const handleOpenCreate = () => {
    setSelectedTemplate(undefined);
    setModalState("details");
  };

  const handleOpenEdit = (template: TemplateResource) => {
    setSelectedTemplate(template);
    setModalState("details");
  };

  const handleClose = () => {
    setModalState("idle");
    setSelectedTemplate(undefined);
  };

  const handleOpenDeleteModal = (template: TemplateResource) => {
    setModalState("delete");
    setSelectedTemplate(template);
  };

  const handleCloseDeleteModal = () => {
    setModalState("idle");
    setSelectedTemplate(undefined);
  };

  const handleCloseActivateModal = () => {
    setModalState("idle");
    setSelectedTemplate(undefined);
  };

  const handleActivateModal = (template: TemplateResource) => {
    setModalState("activate");
    setSelectedTemplate(template);
  };

  const handleActivate = (id: string) => {
    if (id) {
      activateMutation(id, {
        onSuccess: () => {
          showToast({
            title: "Plantilla guardada",
            description: "Los cambios se guardaron correctamente.",
            options: { type: "success", duration: 4000 },
          });
          refetch();
          setModalState("idle");
          setSelectedTemplate(undefined);
        },
      });
    }
  };

  const handleDeactivate = (id: string) => {
    if (id) {
      deActivateMutation(id, {
        onSuccess: () => {
          showToast({
            title: "Plantilla deshabilitada correctamente",
            description: "Los cambios se guardaron correctamente.",
            options: { type: "success", duration: 4000 },
          });
          handleCloseDeleteModal();
          setSelectedTemplate(undefined);
          refetch();
        },
      });
    }
  };

  useEffect(() => {
    setQuery(deferredQuery || null);
    setPage(1);
  }, [deferredQuery, setQuery, setPage]);

  useEffect(() => {
    if (search === "") {
      refetch();
    }
  }, [search, refetch]);

  if (isLoading) {
    return (
      <div className="w-full h-full flex flex-col space-y-2 p-1">
        <TemplatesListHeader />
        <div className="flex justify-between gap-2">{/* Controles */}</div>
        <div className="w-full flex flex-col mt-2 gap-2">
          {[1, 2, 3, 4, 5, 6, 7].map((item) => (
            <Skeleton
              key={item}
              className="w-full gap-4 h-[68px] rounded-xl px-4 py-3"
            />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="w-full h-full flex flex-col space-y-2 p-1">
        <div className="flex flex-col justify-center items-center min-h-[calc(100vh-210px)]">
          <div className="text-center">
            <Icon
              type="fas"
              name="exclamation-triangle"
              className="text-4xl mb-4 text-danger"
            />
            <p className="text-lg font-semibold text-danger">
              Error al cargar las plantillas
            </p>
            <p className="text-sm text-body-quiet mt-2">
              {"Ocurrió un error inesperado"}
            </p>
            <Button
              variant="default"
              size="sm"
              className="mt-4"
              onClick={() => refetch()}
            >
              Reintentar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (templates.length === 0 && filters.length === 0 && !deferredQuery) {
    return (
      <div className="w-full h-full flex flex-col space-y-2 p-1">
        <DocumentModal
          isOpen={modalState === "details"}
          onClose={handleClose}
          canDelete={selectedTemplate?.status === "active"}
          onDelete={
            selectedTemplate
              ? () => handleOpenDeleteModal(selectedTemplate)
              : undefined
          }
        />
        <NoDataToShow />
      </div>
    );
  }

  if (templates.length === 0 && (filters.length > 0 || deferredQuery)) {
    return (
      <div className="w-full h-full flex flex-col space-y-2 p-1">
        <TemplatesListHeader />
        <div className="flex justify-between gap-2">
          <div className="flex space-x-3">
            <SortDropdown
              currentOrderField={orderField}
              currentOrderDirection={orderDirection}
              sortOptions={sortFields}
              onClick={handleApplySort}
            />
            <Filter
              filters={FilterFields}
              currentFilters={filters}
              onCurrentFiltersChange={setFilters}
              selecteds={selectedEntities}
              onSetSelecteds={setSelectedEntities}
            />
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden md:inline">
              <SearchInput
                variant="sm"
                placeholder="Buscar plantillas..."
                className="w-[140px] laptop:w-[240px] text-sm"
                value={search || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.trim() === "" && value !== "") {
                    return;
                  }
                  setSearch(value);
                }}
              />
            </div>
            {canCreate && (
              <Button
                variant="default"
                size="sm"
                className="items-center gap-2 cursor-pointer py-1.5 px-2"
                onClick={handleOpenCreate}
              >
                Nueva plantilla
                <Icon type="far" name="plus" className="text-current" />
              </Button>
            )}
          </div>
        </div>
        <div className="flex flex-col justify-center items-center min-h-[calc(100vh-210px)]">
          <NoData
            title="No se encontraron resultados"
            description={
              deferredQuery
                ? `No hay plantillas que coincidan con "${deferredQuery}"`
                : "No hay plantillas con los filtros aplicados"
            }
          >
            <Button
              variant="quiet"
              size="sm"
              className="items-center gap-2"
              onClick={() => {
                setSearch("");
                setFilters([]);
                setSelectedEntities({});
              }}
            >
              Limpiar búsqueda y filtros
              <Icon type="far" name="times" className="text-current" />
            </Button>
          </NoData>
        </div>
      </div>
    );
  }

  const templateDetails: DocumentDetails | undefined = selectedTemplate
    ? {
        template_id: selectedTemplate.id,
        display_name: selectedTemplate.display_name,
        description: selectedTemplate.description || "",
        status: selectedTemplate.status,
      }
    : undefined;

  return (
    <>
      <DocumentModal
        isOpen={modalState === "details"}
        template={templateDetails}
        onClose={handleClose}
        canDelete={selectedTemplate?.status === "active"}
        onDelete={
          selectedTemplate
            ? () => handleOpenDeleteModal(selectedTemplate)
            : undefined
        }
      />
      <DeleteTemplateModal
        isLoading={isDeactivating}
        open={modalState === "delete"}
        onClose={handleCloseDeleteModal}
        onDelete={() => handleDeactivate(selectedTemplate?.id || "")}
      />
      <ActivateTemplateModal
        isLoading={isActivating}
        open={modalState === "activate"}
        onClose={handleCloseActivateModal}
        onActivate={() => handleActivate(selectedTemplate?.id || "")}
      />
      <div className="w-full h-full flex flex-col space-y-2 p-1">
        <TemplatesListHeader />
        <div className="flex justify-between gap-2">
          <div className="flex space-x-3">
            <SortDropdown
              currentOrderField={orderField}
              currentOrderDirection={orderDirection}
              sortOptions={sortFields}
              onClick={handleApplySort}
            />
            <Filter
              filters={FilterFields}
              currentFilters={filters}
              onCurrentFiltersChange={setFilters}
              selecteds={selectedEntities}
              onSetSelecteds={setSelectedEntities}
            />
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden md:inline">
              <SearchInput
                variant="sm"
                placeholder="Buscar plantillas..."
                className="w-[140px] laptop:w-[240px] text-sm"
                value={search || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.trim() === "" && value !== "") {
                    return;
                  }
                  setSearch(value);
                }}
              />
            </div>
            {canCreate && (
              <Button
                variant="default"
                size="sm"
                className="items-center gap-2 cursor-pointer py-1.5 px-2"
                onClick={handleOpenCreate}
              >
                Nueva plantilla
                <Icon type="far" name="plus" className="text-current" />
              </Button>
            )}
          </div>
        </div>
        <>
          {templates.map((template: TemplateResource) => (
            <NavLink
              key={template.id}
              to={`/plantillas/documentos/${template.id}`}
            >
              <DocumentCard
                key={template.id}
                template={template}
                onSelectEdit={() => handleOpenEdit(template)}
                onSelectDeactivate={() => handleOpenDeleteModal(template)}
                onSelectActivate={() => handleActivateModal(template)}
              />
            </NavLink>
          ))}
          {templatesData?.meta !== undefined &&
            templatesData?.links !== undefined && (
              <div className="p-3">
                <TablePaginantion
                  currentPage={currentPage}
                  totalItems={totalItems}
                  hasNext={hasNextPage}
                  hasPrevious={hasPreviousPage}
                  onNext={onNext}
                  onPageChange={onPageChange}
                  onPrevious={onPrevious}
                  pageSize={actualPageSize}
                  totalPages={totalPages}
                />
              </div>
            )}
        </>
      </div>
    </>
  );
};

const TemplatesListHeader = () => {
  return (
    <p className="font-geist text-body font-normal text-base mt-8 mb-6">
      Crea y edita las plantillas disponibles en tu organización.
    </p>
  );
};
export default DocumentTemplatesList;
